'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import * as CustomPropTypes from '../../constants/PropTypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H1 from 'UIComponents/elements/headings/H1';
import UIButton from 'UIComponents/button/UIButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { getConnectedAppsUrl } from '../../utils/urls';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import SearchSelect from './search/SearchSelect';
import CoreStorefrontHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontHeader';
import { getIsPublic } from '../../selectors/auth';

const getI18nKey = key => `appMarketplace.storefront.header.${key}`;

const HEADER_CUSTOM_STYLE = {
  MAIN_SECTION_CLASSNAME: 'p-top-7',
  BACKGROUND_COLOR: '#0068B1',
  GRADIENT_ANGLE: '225deg',
  START_GRADIENT_COLOR: '#0068B1',
  END_GRADIENT_COLOR: '#5C62D6'
};

const Header = ({
  activeCategory,
  activeSuperCategory,
  appIntegrations,
  headingLevel,
  isAdminPreview,
  isCustomer,
  onSearchAppSelect,
  onSearchCategorySelect,
  onSearchClear,
  onSearchFeatureSelect,
  onSearchFilterSelect,
  onSearchSubmit,
  searchValue,
  trackBeforeUnloadInteraction
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const onStickyChange = useCallback(evt => {
    setIsSticky(evt.target.value);
  }, []);
  const isPublicApp = useSelector(getIsPublic);
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const searchInputSize = useMemo(() => {
    if (!isMobileView && !isSticky) {
      return 'xl';
    }

    return 'default';
  }, [isMobileView, isSticky]);
  const renderedTitle = useMemo(() => {
    const isH2 = headingLevel === 'h2';
    const Heading = isH2 ? 'span' : H1;
    const headingClasses = 'text-center' + (isH2 ? " is--heading-1" : "");
    const ariaLevel = headingLevel === 'h1' ? 1 : null;
    return /*#__PURE__*/_jsx(UIRouterLink, {
      to: "/",
      use: "unstyled",
      children: /*#__PURE__*/_jsx(Heading, {
        "aria-level": ariaLevel,
        className: headingClasses,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('headingText')
        })
      })
    });
  }, [headingLevel]);
  const renderedButtonRow = useMemo(() => /*#__PURE__*/_jsxs(UIButtonWrapper, {
    children: [/*#__PURE__*/_jsx(UIButton, {
      "data-selenium-id": "manage-apps-button",
      disabled: isAdminPreview,
      href: getConnectedAppsUrl(isCustomer),
      onClick: () => {
        trackBeforeUnloadInteraction('click manage apps button');
      },
      size: "extra-small",
      use: "primary-white",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('manageButtonText')
      })
    }), /*#__PURE__*/_jsx(UIButton, {
      disabled: isAdminPreview,
      external: true,
      href: "https://developers.hubspot.com",
      iconProps: {
        className: 'hidden'
      },
      onClick: () => {
        trackBeforeUnloadInteraction('click build button');
      },
      size: "extra-small",
      use: "primary-white",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('buildButtonText')
      })
    })]
  }), [isAdminPreview, isCustomer, trackBeforeUnloadInteraction]);
  return /*#__PURE__*/_jsx(CoreStorefrontHeader, {
    collapsedTitle: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "appMarketplace.detail.header.rootBreadcrumbLinkText"
    }),
    customStyle: HEADER_CUSTOM_STYLE,
    onStickyChange: onStickyChange,
    subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
      message: getI18nKey('subheadingText')
    }),
    title: renderedTitle,
    renderedButtonRow: isPublicApp ? null : renderedButtonRow,
    ungatedMobile: true,
    children: /*#__PURE__*/_jsx(SearchSelect, {
      activeCategory: activeCategory,
      activeSuperCategory: activeSuperCategory,
      appIntegrations: appIntegrations,
      inputSize: searchInputSize,
      isSticky: isSticky,
      onAppSelect: onSearchAppSelect,
      onCategorySelect: onSearchCategorySelect,
      onClear: onSearchClear,
      onFeatureSelect: onSearchFeatureSelect,
      onFilterSelect: onSearchFilterSelect,
      onSubmit: onSearchSubmit,
      readOnly: isAdminPreview,
      searchValue: searchValue
    })
  });
};

Header.propTypes = {
  activeCategory: CustomPropTypes.category,
  activeSuperCategory: CustomPropTypes.superCategory,
  appIntegrations: ImmutablePropTypes.orderedSet.isRequired,
  headingLevel: PropTypes.oneOf(['h1', 'h2']),
  isAdminPreview: PropTypes.bool,
  isCustomer: PropTypes.bool.isRequired,
  onSearchAppSelect: PropTypes.func.isRequired,
  onSearchCategorySelect: PropTypes.func.isRequired,
  onSearchClear: PropTypes.func.isRequired,
  onSearchFeatureSelect: PropTypes.func.isRequired,
  onSearchFilterSelect: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  trackBeforeUnloadInteraction: PropTypes.func.isRequired
};
Header.defaultProps = {
  headingLevel: 'h1'
};
export default Header;